import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, FileText, BookOpen, TrendingUp, Layers, CheckCircle2,
  Users, Globe2, Compass, Plus, Trash2, RefreshCw, Check, ArrowRight, Loader2, X,
} from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync, generateQuickStartDraft } from "./utils";

const EMPTY_DRAFT = {
  vision: "", mission: "", values: [], cultureStatement: "",
  cultureThemes: ["", "", ""], ultimateOutcome: "", studentOutcome: "", institutionalOutcome: "",
  parentExpectations: "", communityNeeds: "", futureDemand: "", skillTags: [], strategicRecommendation: "",
};

const labelClass = "text-[12px] font-semibold text-[#233445] mb-1 flex items-center gap-1";
const inputClass = "h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] w-full";
const textareaClass = "border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y w-full";

const FLOW_STEPS = [
  { key: "info", label: "School Info", icon: FileText },
  { key: "identity", label: "Identity Drafted", icon: BookOpen },
  { key: "market", label: "Market Mapped", icon: TrendingUp },
  { key: "frameworks", label: "Frameworks Aligned", icon: Layers },
  { key: "ready", label: "SDP Ready", icon: CheckCircle2 },
];

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-[#1D3469] mb-[2px]">{title}</div>
          {note && <div className="text-sm text-gray-500">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export default function QuickStartLanding() {
  const settings = getSchoolSettings();

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(0); // 0 = School Info, 1 = Priorities
  const [schoolName, setSchoolName] = useState(settings.schoolName || "");
  const [schoolType, setSchoolType] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState("");

  const [stageIndex, setStageIndex] = useState(0); // progress through FLOW_STEPS
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [draft, setDraft] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const identity = (await loadModuleData("identity", {})) || {};
        const market = (await loadModuleData("market", {})) || {};
        const hasIdentity = !!(identity.vision || identity.mission);
        const hasMarket = !!(market.parentExpectations || market.communityNeeds || market.strategicRecommendation);

        if (hasIdentity || hasMarket) {
          setDraft({
            vision: identity.vision || "",
            mission: identity.mission || "",
            values: Array.isArray(identity.values) ? identity.values : [],
            cultureStatement: identity.cultureStatement || "",
            cultureThemes: Array.isArray(identity.cultureThemes) && identity.cultureThemes.length ? identity.cultureThemes : ["", "", ""],
            ultimateOutcome: identity.ultimateOutcome || "",
            studentOutcome: identity.studentOutcome || "",
            institutionalOutcome: identity.institutionalOutcome || "",
            parentExpectations: market.parentExpectations || "",
            communityNeeds: market.communityNeeds || "",
            futureDemand: market.futureDemand || "",
            skillTags: [],
            strategicRecommendation: market.strategicRecommendation || "",
          });
          setStageIndex(4);
          setSubmittedSummary({
            schoolName: settings.schoolName || "",
            schoolType: "",
            description: identity.remarks || "",
          });
        }
      } finally {
        setLoadingExisting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openModal() {
    setFormError("");
    setStep(0);
    setModalOpen(true);
  }

  function goNext() {
    if (!schoolName.trim()) {
      setFormError("School Name is required before moving ahead.");
      return;
    }
    setFormError("");
    setStep(1);
  }

  async function handleGenerate() {
    if (!schoolName.trim()) {
      setFormError("School Name is required.");
      setStep(0);
      return;
    }
    setFormError("");
    setModalOpen(false);
    setSubmittedSummary({ schoolName, schoolType, description });
    setSaved(false);
    setEditMode(false);
    setGenError("");
    setDraft(null);
    setGenerating(true);
    setStageIndex(1);

    const result = await generateQuickStartDraft(schoolName, schoolType, description);

    if (!result) {
      setGenError("Could not generate your SDP draft right now. Please try again.");
      setGenerating(false);
      setStageIndex(0);
      return;
    }

    setDraft({
      vision: result.vision || "",
      mission: result.mission || "",
      values: Array.isArray(result.values) ? result.values : [],
      cultureStatement: result.cultureStatement || "",
      cultureThemes: Array.isArray(result.cultureThemes) && result.cultureThemes.length ? result.cultureThemes : ["", "", ""],
      ultimateOutcome: result.ultimateOutcome || "",
      studentOutcome: result.studentOutcome || "",
      institutionalOutcome: result.institutionalOutcome || "",
      parentExpectations: result.parentExpectations || "",
      communityNeeds: result.communityNeeds || "",
      futureDemand: result.futureDemand || "",
      skillTags: Array.isArray(result.skillTags) ? result.skillTags : [],
      strategicRecommendation: result.strategicRecommendation || "",
    });
    setStageIndex(4);
    setGenerating(false);
  }

  function updateValue(idx, field, val) {
    setDraft((d) => ({ ...d, values: d.values.map((v, i) => (i === idx ? { ...v, [field]: val } : v)) }));
  }
  function addValue() {
    setDraft((d) => ({ ...d, values: [...d.values, { name: "", desc: "" }] }));
  }
  function removeValue(idx) {
    setDraft((d) => ({ ...d, values: d.values.filter((_, i) => i !== idx) }));
  }
  function updateCultureTheme(idx, val) {
    setDraft((d) => {
      const themes = [...d.cultureThemes];
      themes[idx] = val;
      return { ...d, cultureThemes: themes };
    });
  }

  async function handleSaveEverything() {
    setSaving(true);
    try {
      const info = submittedSummary || { schoolName, schoolType, description };
      if (info.schoolName || info.schoolType) {
        const existingSchoolInfo = (await loadModuleData("schoolInfo", {})) || {};
        const updatedSchoolInfo = {
          ...existingSchoolInfo,
          schoolName: info.schoolName || existingSchoolInfo.schoolName || "",
          schoolType: info.schoolType || existingSchoolInfo.schoolType || "",
        };
        await saveModuleData("schoolInfo", updatedSchoolInfo, 0);
      }

      const existingIdentity = (await loadModuleData("identity", {})) || {};
      const updatedIdentity = {
        ...existingIdentity,
        vision: draft.vision,
        mission: draft.mission,
        values: draft.values,
        cultureStatement: draft.cultureStatement,
        cultureThemes: draft.cultureThemes,
        ultimateOutcome: draft.ultimateOutcome,
        studentOutcome: draft.studentOutcome,
        institutionalOutcome: draft.institutionalOutcome,
        remarks: description || existingIdentity.remarks || "",
      };
      await saveModuleData("identity", updatedIdentity, 0);

      const existingMarket = (await loadModuleData("market", {})) || {};
      const updatedMarket = {
        ...existingMarket,
        parentExpectations: draft.parentExpectations,
        communityNeeds: draft.communityNeeds,
        futureDemand: draft.futureDemand,
        strategicRecommendation: draft.strategicRecommendation,
        competitors: Array.isArray(existingMarket.competitors) ? existingMarket.competitors : [],
        signals: Array.isArray(existingMarket.signals) ? existingMarket.signals : [],
      };
      await saveModuleData("market", updatedMarket, 0);

      await addActivityAsync("Quick Start", "School Information, Identity + Market Dynamics drafted with AI Quick Start", "Saved", "green");
      showToast("SDP saved to School Information, Institutional Identity and Market Dynamics.", "success");
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">
      <ToastContainer />
      <div className="flex justify-center pl-10 pr-6 py-6">
        <main className="w-full font-[poppins]">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">AI Quick Start</h1>
            <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 mb-8 leading-[1.4]">
              Give us your school's name and priorities — AI instantly drafts your complete Institutional Identity and Market Dynamics.
            </p>
          </div>

          {loadingExisting ? (
            <div className="rounded-2xl border border-[#e6ecf3] bg-white px-6 py-10 text-center">
              <Loader2 className="w-6 h-6 text-[#f47c20] animate-spin mx-auto mb-3" />
              <p className="text-sm text-[#617587]">Loading your school's SDP status...</p>
            </div>
          ) : (
          <>
          {/* Flow overview */}
          <Window title="One-Click School Destiny Plan" note="School info flows into the SDP Engine, producing Vision, Mission, Identity, Culture and Market Dynamics together.">
            <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1">
              {FLOW_STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i < stageIndex || (i === stageIndex && !generating && draft);
                const active = i === stageIndex && generating;
                return (
                  <React.Fragment key={s.key}>
                    <div className="flex flex-col items-center gap-2 min-w-[110px]">
                      <div
                        className={`w-12 h-12 rounded-full grid place-items-center border transition-colors duration-200 ${
                          done
                            ? "bg-[#fff4eb] border-[#f7c9a1] text-[#f47c20]"
                            : active
                              ? "bg-[#eef6fc] border-[#cfe3f3] text-[#2d81c1]"
                              : "bg-[#f7f9fc] border-[#e6ecf3] text-[#8ea0b1]"
                        }`}
                      >
                        {active ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span className={`text-[12px] font-medium text-center ${done ? "text-[#233445]" : "text-[#8ea0b1]"}`}>{s.label}</span>
                    </div>
                    {i < FLOW_STEPS.length - 1 && (
                      <div className={`flex-1 h-[2px] rounded-full mb-6 ${i < stageIndex ? "bg-[#f7c9a1]" : "bg-[#e6ecf3]"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </Window>

          {submittedSummary && (
            <div className="mt-6 rounded-2xl border border-[#f7c9a1] bg-gradient-to-r from-[#fff7f0] to-[#fff] px-6 py-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#f47c20] uppercase tracking-wide">
                  <CheckCircle2 className="w-3.5 h-3.5" /> School context received
                </span>
                <h3 className="text-base font-bold text-[#233445] mt-1">
                  {submittedSummary.schoolName}{submittedSummary.schoolType ? ` — ${submittedSummary.schoolType}` : ""}
                </h3>
                <p className="text-sm text-[#617587] mt-0.5">{submittedSummary.description || "No extra priorities added."}</p>
              </div>
              <button
                onClick={openModal}
                className="flex items-center gap-2 px-5 py-3 rounded-[12px] bg-white text-[#233445] border border-[#e6ecf3] font-semibold text-sm hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors"
              >
                Update school info
              </button>
            </div>
          )}

          <div className="mt-6">
            <Window
              title="One-Click School Context Input"
              note="Enter your school's name and priorities — AI drafts Vision, Mission, Values, Culture and Market Dynamics in one pass."
              headerRight={
                <button
                  onClick={openModal}
                  className="flex items-center gap-2 px-5 py-3 rounded-[12px] bg-[#f47c20] text-white font-semibold text-sm hover:bg-[#e46c13] transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> {submittedSummary ? "Regenerate AI SDP Output" : "Generate AI SDP Output"}
                </button>
              }
            >
              <p className="text-sm text-[#617587]">
                This uses the same AI drafting engine as the Institutional Identity page's Quick Start, but also drafts your Market Dynamics
                (Parent Expectations, Community Needs, Future Demand Areas, Strategic Recommendation) together in a single pass.
              </p>
            </Window>
          </div>

          {generating && (
            <div className="mt-6 rounded-2xl border border-[#e6ecf3] bg-white px-6 py-10 text-center">
              <Loader2 className="w-6 h-6 text-[#f47c20] animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#233445]">Building your School Destiny Plan...</p>
              <p className="text-xs text-[#617587] mt-1">Mapping school info with frameworks, identity and market context.</p>
            </div>
          )}

          {genError && (
            <div className="mt-6 rounded-2xl border border-[#f4c5c5] bg-[#fff0f0] px-6 py-4 text-sm text-[#d95353]">{genError}</div>
          )}

          {draft && !generating && (
            <div className="mt-6">
              <Window
                title="Generated SDP Intelligence"
                note="AI-drafted Institutional Identity and Market Dynamics, ready to review and save."
                headerRight={
                  <>
                    <button
                      onClick={() => setEditMode((v) => !v)}
                      className="min-h-[40px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors"
                    >
                      {editMode ? "Done editing" : "Edit before saving"}
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="min-h-[40px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors flex items-center gap-1.5"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                    </button>
                    <button
                      onClick={handleSaveEverything}
                      disabled={saving}
                      className="min-h-[40px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save Everything"}
                    </button>
                  </>
                }
              >
                {saved ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-8 h-8 text-[#2d9b68] mx-auto mb-3" />
                    <p className="text-sm text-[#233445] mb-4">Saved. Your School Information, Institutional Identity and Market Dynamics have been updated.</p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <Link to="/sdp/school-information" className="flex items-center gap-1 px-4 py-2 rounded-[10px] text-sm font-semibold border border-[#e6ecf3] text-[#233445] hover:bg-gray-50">
                        View School Information <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link to="/sdp/institutional-identity" className="flex items-center gap-1 px-4 py-2 rounded-[10px] text-sm font-semibold bg-[#f47c20] text-white hover:bg-[#e46c13]">
                        View Institutional Identity <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link to="/sdp/market-dynamics" className="flex items-center gap-1 px-4 py-2 rounded-[10px] text-sm font-semibold border border-[#e6ecf3] text-[#233445] hover:bg-gray-50">
                        View Market Dynamics <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ) : !editMode ? (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BookOpen className="w-4 h-4 text-[#f47c20]" />
                        <span className="text-sm font-bold text-[#1D3469] uppercase tracking-wide">Institutional Identity</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="w-4 h-4 text-[#2d81c1]" />
                            <span className="text-sm font-semibold text-[#233445]">Vision & Mission</span>
                          </div>
                          <p className="text-xs text-[#617587] mb-1"><b>Vision:</b> {draft.vision}</p>
                          <p className="text-xs text-[#617587]"><b>Mission:</b> {draft.mission}</p>
                        </div>
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Layers className="w-4 h-4 text-[#d58d1f]" />
                            <span className="text-sm font-semibold text-[#233445]">Values & Culture</span>
                          </div>
                          <p className="text-xs text-[#617587] mb-2">{draft.cultureStatement}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {draft.values.map((v, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]">{v.name}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-[#f47c20]" />
                        <span className="text-sm font-bold text-[#1D3469] uppercase tracking-wide">Market Dynamics</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-[#2d81c1]" />
                            <span className="text-sm font-semibold text-[#233445]">Parent Expectations</span>
                          </div>
                          <p className="text-xs text-[#617587]">{draft.parentExpectations}</p>
                        </div>
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Globe2 className="w-4 h-4 text-[#2d9b68]" />
                            <span className="text-sm font-semibold text-[#233445]">Community Needs</span>
                          </div>
                          <p className="text-xs text-[#617587]">{draft.communityNeeds}</p>
                        </div>
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-[#f47c20]" />
                            <span className="text-sm font-semibold text-[#233445]">Future Demand Areas</span>
                          </div>
                          <p className="text-xs text-[#617587] mb-2">{draft.futureDemand}</p>
                          {draft.skillTags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {draft.skillTags.map((t, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="border border-[#e6ecf3] rounded-[14px] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Compass className="w-4 h-4 text-[#d58d1f]" />
                            <span className="text-sm font-semibold text-[#233445]">Strategic Recommendation</span>
                          </div>
                          <p className="text-xs text-[#617587]">{draft.strategicRecommendation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="text-sm font-bold text-[#1D3469] uppercase tracking-wide">Institutional Identity</div>

                    <div>
                      <label className={labelClass}>Vision</label>
                      <textarea value={draft.vision} onChange={(e) => setDraft((d) => ({ ...d, vision: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Mission</label>
                      <textarea value={draft.mission} onChange={(e) => setDraft((d) => ({ ...d, mission: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className={labelClass}>Values</label>
                        <button onClick={addValue} className="flex items-center gap-1 text-xs font-semibold text-[#2d81c1] hover:underline"><Plus className="w-3.5 h-3.5" /> Add Value</button>
                      </div>
                      <div className="space-y-2">
                        {draft.values.map((v, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input value={v.name} onChange={(e) => updateValue(i, "name", e.target.value)} placeholder="Value name" className={`${inputClass} w-1/3`} />
                            <input value={v.desc} onChange={(e) => updateValue(i, "desc", e.target.value)} placeholder="Short descriptor" className={inputClass} />
                            <button onClick={() => removeValue(i)} className="p-2 text-red-400 hover:text-red-600" aria-label="Remove value"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Culture Statement</label>
                      <textarea value={draft.cultureStatement} onChange={(e) => setDraft((d) => ({ ...d, cultureStatement: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Culture Themes</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[0, 1, 2].map((i) => (
                          <input key={i} value={draft.cultureThemes[i] || ""} onChange={(e) => updateCultureTheme(i, e.target.value)} placeholder={`Theme ${i + 1}`} className={inputClass} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Ultimate Outcome</label>
                      <textarea value={draft.ultimateOutcome} onChange={(e) => setDraft((d) => ({ ...d, ultimateOutcome: e.target.value }))} rows={2} className={textareaClass} />
                    </div>

                    <div className="text-sm font-bold text-[#1D3469] uppercase tracking-wide pt-2 border-t border-[#e6ecf3]">Market Dynamics</div>

                    <div>
                      <label className={labelClass}>Parent Expectations</label>
                      <textarea value={draft.parentExpectations} onChange={(e) => setDraft((d) => ({ ...d, parentExpectations: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Community Needs</label>
                      <textarea value={draft.communityNeeds} onChange={(e) => setDraft((d) => ({ ...d, communityNeeds: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Future Demand Area</label>
                      <input value={draft.futureDemand} onChange={(e) => setDraft((d) => ({ ...d, futureDemand: e.target.value }))} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Strategic Recommendation</label>
                      <textarea value={draft.strategicRecommendation} onChange={(e) => setDraft((d) => ({ ...d, strategicRecommendation: e.target.value }))} rows={2} className={textareaClass} />
                    </div>
                  </div>
                )}
              </Window>
            </div>
          )}
          </>
          )}
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto font-[poppins]">
            <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-gray-200 bg-[#fff7f0] sticky top-0 z-10">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#f47c20] uppercase tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" /> AI Quick Start
                </span>
                <h2 className="text-xl font-bold text-[#233445] mt-1">Tell us about your school</h2>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-2 px-6 pt-5">
              <button
                onClick={() => setStep(0)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${step === 0 ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-white text-[#617587] border-[#e6ecf3]"}`}
              >
                01 School Info
              </button>
              <button
                onClick={() => schoolName.trim() && setStep(1)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${step === 1 ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-white text-[#617587] border-[#e6ecf3]"}`}
              >
                02 Priorities
              </button>
            </div>

            <div className="px-6 py-6">
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>School Name</label>
                    <input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="e.g. ABC Public School" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>School Type</label>
                    <input value={schoolType} onChange={(e) => setSchoolType(e.target.value)} placeholder="e.g. K-12 CBSE school, International School, Play School..." className={inputClass} />
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Description / Priorities</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Example: improve admissions, strengthen STEM, reduce teacher workload, increase parent trust..."
                      rows={4}
                      className={textareaClass}
                    />
                  </div>
                </div>
              )}
              {formError && <p className="text-sm text-red-600 mt-3">{formError}</p>}
            </div>

            <div className="flex items-center justify-end gap-2 px-6 pb-6">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-[10px] text-sm font-medium text-[#617587] border border-[#e6ecf3] hover:bg-gray-50">
                Cancel
              </button>
              {step === 0 && (
                <button onClick={goNext} className="px-4 py-2 rounded-[10px] text-sm font-semibold bg-[#f47c20] text-white hover:bg-[#e46c13]">
                  Next: Priorities
                </button>
              )}
              {step === 1 && (
                <button onClick={handleGenerate} className="flex items-center gap-1.5 px-4 py-2 rounded-[10px] text-sm font-semibold bg-[#f47c20] text-white hover:bg-[#e46c13]">
                  <Sparkles className="w-4 h-4" /> Generate AI SDP Output
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
